import React, { useState } from 'react';
import Modal from './Modal';
import Button from './Button';

const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

/**
 * ShareSheet - Modal with event info card, copy actions, and share targets
 * Brutalist style: heavy borders, monospace, high-contrast
 */
export default function ShareSheet({ isOpen, onClose, title, url, inviteCode, date, status, mode }) {
  const [copyLinkSuccess, setCopyLinkSuccess] = useState(false);
  const [copyCodeSuccess, setCopyCodeSuccess] = useState(false);

  const shareText = `You're invited to ${title} on PartyResume! Apply here: ${url}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyLinkSuccess(true);
      setTimeout(() => setCopyLinkSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(inviteCode);
      setCopyCodeSuccess(true);
      setTimeout(() => setCopyCodeSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSMS = async () => {
    const smsBody = encodeURIComponent(shareText);
    if (isMobile) {
      window.location.href = isIOS ? `sms:&body=${smsBody}` : `sms:?body=${smsBody}`;
    } else {
      try {
        await navigator.clipboard.writeText(shareText);
        setCopyLinkSuccess(true);
        setTimeout(() => setCopyLinkSuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener,noreferrer');
  };

  const handleX = () => {
    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(shareText);
    window.open(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, '_blank', 'noopener,noreferrer');
  };

  const handleFacebook = () => {
    const encodedUrl = encodeURIComponent(url);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank', 'noopener,noreferrer');
  };

  const getStatusBadge = () => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800 border-gray-500',
      pledging: 'bg-blue-100 text-blue-800 border-blue-500',
      processing_greenlight: 'bg-yellow-100 text-yellow-800 border-yellow-500',
      greenlit: 'bg-green-100 text-green-800 border-green-500',
      ended: 'bg-purple-100 text-purple-800 border-purple-500',
      canceled: 'bg-red-100 text-red-800 border-red-500',
      published: 'bg-green-100 text-green-800 border-green-500',
    };
    return styles[status] || styles.draft;
  };

  const formattedDate = date
    ? new Date(date).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Date TBD';

  const targets = [
    {
      label: 'SMS',
      onClick: handleSMS,
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="square" strokeLinejoin="miter" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
    },
    {
      label: 'WHATSAPP',
      onClick: handleWhatsApp,
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      ),
    },
    {
      label: 'X',
      onClick: handleX,
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
    },
    {
      label: 'FACEBOOK',
      onClick: handleFacebook,
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
    },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="SHARE EVENT" size="lg">
      {/* Event info card */}
      <div className="border-2 border-black p-6 mb-6">
        <h3 className="font-mono text-lg font-bold uppercase tracking-wider text-black mb-2">
          {title}
        </h3>
        <p className="font-mono text-sm text-gray-600 mb-4">
          {formattedDate}
        </p>
        <div className="flex items-center gap-2">
          <span
            className={`px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider border ${getStatusBadge()}`}
          >
            {status || 'DRAFT'}
          </span>
          <span className="px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider border border-black bg-gray-100">
            {mode === 'curated' ? 'CURATED' : 'OPEN'}
          </span>
        </div>
      </div>

      {/* Event Link section */}
      <div className="mb-6">
        <label className="block font-mono text-sm font-semibold uppercase tracking-wider mb-2 text-black">
          Event Link
        </label>
        <div className="flex gap-2">
          <div className="flex-1 px-4 py-3 border-2 border-black bg-gray-50 overflow-hidden">
            <p className="font-mono text-sm text-black truncate">{url}</p>
          </div>
          <Button
            variant={copyLinkSuccess ? 'primary' : 'secondary'}
            onClick={handleCopyLink}
          >
            {copyLinkSuccess ? 'COPIED!' : 'COPY LINK'}
          </Button>
        </div>
      </div>

      {/* Invite Code section */}
      <div className="mb-6">
        <label className="block font-mono text-sm font-semibold uppercase tracking-wider mb-2 text-black">
          Invite Code
        </label>
        <div className="flex gap-2">
          <div className="flex-1 px-4 py-3 border-2 border-black bg-gray-50">
            <p className="font-mono text-2xl font-bold tracking-widest text-black text-center">
              {inviteCode}
            </p>
          </div>
          <Button
            variant={copyCodeSuccess ? 'primary' : 'secondary'}
            onClick={handleCopyCode}
          >
            {copyCodeSuccess ? 'COPIED!' : 'COPY CODE'}
          </Button>
        </div>
        <p className="mt-2 font-mono text-xs text-gray-500">
          Guests can use this code to find and apply to your event
        </p>
      </div>

      {/* Share platform buttons */}
      <div className="flex justify-center gap-3 flex-wrap pt-6 border-t-2 border-black">
        {targets.map((target) => (
          <button
            key={target.label}
            type="button"
            onClick={target.onClick}
            className="flex flex-col items-center gap-2 group"
          >
            <div className="w-14 h-14 border-2 border-black flex items-center justify-center bg-white hover:bg-black hover:text-white text-black transition-colors duration-200">
              {target.icon}
            </div>
            <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-600 group-hover:text-black transition-colors">
              {target.label}
            </span>
          </button>
        ))}
      </div>
    </Modal>
  );
}
